import { useState } from "react";
import { BookOpen, Loader2, Send, Sparkles, Zap } from "lucide-react";
import { ArbiterShell } from "@/components/ArbiterShell";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { askJurisIA, type RechercheResponse } from "@/lib/api";
import { classifyApiError } from "@/hooks/useApiErrorState";
import { isIaV2Enabled, setIaV2EnabledLocal } from "@/lib/featureFlags";
import { AIAssistantV2 } from "@/components/ai-assistant-v2/AIAssistantV2";
import { GenerationProgress } from "@/components/ai-assistant-v2/GenerationProgress";

/**
 * Page Recherche juridique IA.
 *
 * Lot 7 : ajoute un toggle v1 ↔ v2 qui survit à un refresh (localStorage).
 * Par défaut, v1 reste actif tant que `VITE_IA_V2_ENABLED=true` n'est
 * pas posé côté build, ou que l'utilisateur ne flippe pas le switch.
 *
 * Le composant v2 (`AIAssistantV2`) est monté conditionnellement —
 * zéro impact sur les bundles v1 quand le toggle est OFF (sauf l'import
 * statique, mais React/Vite tree-shake les sous-modules inutilisés
 * dès qu'ils ne sont pas référencés en runtime via lazy()).
 */
export default function Recherche() {
  const [v2Enabled, setV2Enabled] = useState<boolean>(() => isIaV2Enabled());

  const handleToggle = (checked: boolean) => {
    setV2Enabled(checked);
    setIaV2EnabledLocal(checked);
  };

  return (
    <ArbiterShell title="Recherche juridique IA">
      <div className="flex-1 px-4 sm:px-6 py-6 sm:py-8 max-w-3xl mx-auto w-full space-y-6">
        <header>
          <div
            className="flex items-center gap-2 text-caption-arbitre"
            style={{ color: "var(--ai-violet)" }}
          >
            <Sparkles size={12} />
            Moteur RAG corpus public OHADA
          </div>
          <h1 className="text-h1-arbitre mt-1">Posez votre question</h1>
          <p
            className="text-body-arbitre mt-2"
            style={{ color: "var(--arbitre-slate-300)", lineHeight: 1.55 }}
          >
            Réponses sourcées vers le corpus OHADA / CCJA. Toutes les
            interactions sont journalisées (AI Act Art. 13) et purgeables
            depuis votre profil (RGPD Art. 17).
          </p>
        </header>

        {/* Toggle v1 ↔ v2 — visible en perm pour permettre la bascule */}
        <div
          className="flex items-center justify-between gap-3 rounded-md border p-3"
          style={{
            borderColor: "var(--graphite-600)",
            background: v2Enabled
              ? "color-mix(in oklch, var(--ai-violet) 8%, transparent)"
              : "var(--graphite-800)",
          }}
        >
          <div className="flex items-center gap-2 text-xs">
            <Zap
              size={13}
              style={{
                color: v2Enabled ? "var(--ai-violet)" : "var(--arbitre-slate-500)",
              }}
            />
            <span style={{ color: "var(--arbitre-slate-100)" }}>
              Mode <strong>{v2Enabled ? "v2" : "v1"}</strong> —{" "}
              <span style={{ color: "var(--arbitre-slate-300)" }}>
                {v2Enabled
                  ? "syllogisme imposé + claims atomiques + critique"
                  : "réponse classique sourcée (compatibilité historique)"}
              </span>
            </span>
          </div>
          <div className="flex items-center gap-2">
            <Label
              htmlFor="ia-v2-toggle"
              className="text-[11px]"
              style={{ color: "var(--arbitre-slate-300)" }}
            >
              v2
            </Label>
            <Switch
              id="ia-v2-toggle"
              checked={v2Enabled}
              onCheckedChange={handleToggle}
              aria-label="Activer l'assistant IA v2 (syllogisme + citations atomiques)"
            />
          </div>
        </div>

        {v2Enabled ? <AIAssistantV2 /> : <ClassicRecherche />}
      </div>
    </ArbiterShell>
  );
}

/**
 * Bloc v1 historique — conservé tel quel pour ne rien casser tant que
 * v2 n'est pas le défaut. À supprimer après un sprint de bake en
 * production v2 (Lot 7.bis).
 */
function ClassicRecherche() {
  const [question, setQuestion] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [response, setResponse] = useState<RechercheResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  const onSubmit = async () => {
    if (question.trim().length < 3 || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      const res = await askJurisIA(question.trim());
      setResponse(res);
    } catch (err) {
      const classified = classifyApiError(err);
      if (classified.kind !== "none") {
        setError(classified.detail);
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <div className="gsf-surface p-5">
        <Textarea
          value={question}
          onChange={(e) => setQuestion(e.target.value)}
          placeholder="Ex : quelles sont les conditions d'application de l'article 13 AUDA ?"
          rows={4}
          disabled={submitting}
          className="resize-none"
        />
        <div className="flex justify-between items-center mt-3 gap-3">
          <p
            className="text-xs"
            style={{ color: "var(--arbitre-slate-500)" }}
          >
            Réponse traitée par l'IA + journalisée.
          </p>
          <Button
            onClick={() => void onSubmit()}
            disabled={submitting || question.trim().length < 3}
            style={{
              background: "var(--ai-violet)",
              color: "var(--arbitre-slate-100)",
            }}
          >
            {submitting ? (
              <>
                <Loader2 size={14} className="animate-spin mr-2" />
                Génération…
              </>
            ) : (
              <>
                <Send size={14} className="mr-2" />
                Soumettre
              </>
            )}
          </Button>
        </div>
      </div>

      {submitting && (
        <div className="mt-4">
          <GenerationProgress variant="recherche-v1" />
        </div>
      )}

      {error && (
        <div
          className="rounded-md border p-5 text-sm mt-4"
          style={{
            borderColor:
              "color-mix(in oklch, var(--block-red) 40%, transparent)",
            background:
              "color-mix(in oklch, var(--block-red) 10%, transparent)",
            color: "rgb(254 202 202)",
          }}
        >
          {error}
        </div>
      )}

      {response && (
        <div className="gsf-surface p-6 space-y-4 mt-4">
          <div
            className="flex items-center justify-between text-xs"
            style={{ color: "var(--arbitre-slate-300)" }}
          >
            <span>
              Modèle :{" "}
              <code className="gsf-mono-proof text-[11px]">
                {response.model}
              </code>
            </span>
            {response.simulated && (
              <span style={{ color: "var(--alert-orange)" }}>
                Mode simulation
              </span>
            )}
          </div>
          <p
            className="text-sm whitespace-pre-wrap"
            style={{ color: "var(--arbitre-slate-100)", lineHeight: 1.6 }}
          >
            {response.answer}
          </p>
          {response.sources_used.length > 0 && (
            <section>
              <h3
                className="flex items-center gap-2 text-caption-arbitre mb-2"
                style={{ color: "var(--proof-gold)" }}
              >
                <BookOpen size={12} />
                Sources ({response.sources_used.length})
              </h3>
              <ul className="space-y-2">
                {response.sources_used.map((s) => (
                  <li
                    key={s.id}
                    className="text-xs rounded-md p-3"
                    style={{
                      background:
                        "color-mix(in oklch, var(--graphite-700) 60%, transparent)",
                      color: "var(--arbitre-slate-300)",
                      lineHeight: 1.5,
                    }}
                  >
                    <code
                      className="gsf-mono-proof text-[10px] block mb-1"
                      style={{ color: "var(--proof-gold)" }}
                    >
                      {s.source}
                    </code>
                    {s.text}
                  </li>
                ))}
              </ul>
            </section>
          )}
        </div>
      )}
    </>
  );
}
